import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, Dropdown, Grid, Header, Input, Pagination, Segment, Table } from 'semantic-ui-react';
import { PaginationProps } from 'semantic-ui-react/dist/commonjs/addons/Pagination/Pagination';
import moment from 'moment';
import { Push, push } from 'connected-react-router';
import {
  EEhsStatus,
  EPtwStatus,
  EUserGroup,
  EUserLevels,
  IBuilding,
  IEhs,
  IProject,
  IPtw,
  IPtwFile,
  IPtwHistoryStatus,
  IUser,
  IWorkstation,
  statusMap,
} from '@relab/alstom-ptw-shared';
import CsvDownloader from 'react-csv-downloader';

import { appState } from '../../redux/selectors';
import {
  buildingsAction,
  ptwAction,
  ptwDeleteAction,
  ptwEditAction,
  ptwUpdateAction,
  usersAction,
  workstationsAction,
} from '../../redux/actions';
import { PtwEditModal } from './PtwEditModal';
import { RootState } from '../../redux/types';
import { client } from '../../redux/store';
import { EhsCreateModal } from '../ehs/EhsCreateModal';
import { TranslationComponent } from '../TranslationComponent';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';
import { PtwsListStatusComponent } from './PtwsListStatusComponent';
import { PtwDeleteModal } from './PtwDeleteModal';
import { PtwStatusDropdown } from '../PtwStatusDropdown';
import { ptwUtils } from '../../helpers/ptwUtils';
import { FileDownloadLink } from '../FileDownloadLink';

const csvColumns = [
  {
    id: 'sequentialNumber',
    displayName: 'Sequential number',
  },
  {
    id: 'name',
    displayName: 'Name',
  },
  {
    id: 'startDate',
    displayName: 'Valid from',
  },
  {
    id: 'endDate',
    displayName: 'Valid until',
  },
  {
    id: 'company',
    displayName: 'Company',
  },
  {
    id: 'train',
    displayName: 'Train',
  },
  {
    id: 'vehicles',
    displayName: 'Vehicles',
  },
  {
    id: 'building',
    displayName: 'Building',
  },
  {
    id: 'workstation',
    displayName: 'Workstation',
  },
  {
    id: 'people',
    displayName: 'People',
  },
  {
    id: 'description',
    displayName: 'Description',
  },
  {
    id: 'createdAt',
    displayName: 'Created At',
  },
  {
    id: 'closingDate',
    displayName: 'Closing Date',
  },
  {
    id: 'issuer',
    displayName: 'Issuer',
  },
  {
    id: 'status',
    displayName: 'Status',
  },
  {
    id: 'requiredTesting',
    displayName: 'Testing',
  },
  {
    id: 'testPassed',
    displayName: 'Testing result',
  },
  {
    id: 'pc9',
    displayName: 'PC9',
  },
  {
    id: 'extraWork',
    displayName: 'Extra work',
  },
  {
    id: 'requiredQualityTesting',
    displayName: 'Quality Testing',
  },
  {
    id: 'ehs',
    displayName: 'EHS',
  },
  {
    id: 'completionStatus',
    displayName: 'Completion Statuses',
  },
  {
    id: 'deletionNotes',
    displayName: 'Deletion notes',
  },
  {
    id: 'ptwHistory',
    displayName: 'Permit History',
  },
];

const pageSize = 50;

function extractClosingDate(ptwHistory: IPtwHistoryStatus[]) {
  let closingDate = ptwHistory.find((history) => history.status === EPtwStatus.CHIUSURA_Y)?.datetime;
  if (closingDate) {
    closingDate = moment(closingDate).format('DD/MM/YYYY');
  }
  return closingDate;
}

export interface IPtwPrettied {
  _ptw: IPtw;
  sequentialNumber: string;
  name: string;
  startDate: string;
  endDate: string;
  company: string;
  train: string;
  vehicles: string[];
  building: string;
  workstation: string;
  people: string;
  description: string;
  createdAt: string;
  closingDate: string;
  issuer: string;
  status: string;
  qualifiedUsers: IUser[];
  ehs?: IEhs;
  requiredTesting: 'YES' | 'NO';
  testPassed: '' | 'Test passed' | 'Test not passed';
  requiredQualityTesting: 'YES' | 'NO';
  extraWork: 'YES' | 'NO';
  pc9: '' | 'Pre PC9' | 'Post PC9';
  completionStatus: string;
  ptwHistory: string;
  deletionNotes: string;
}

interface PtwsListComponentProps {
  i18n: typeof i18n;
  t: typeof i18n.t;
  projectId: string;
  project: IProject;
  buildings: IBuilding[];
  workstations: IWorkstation[];
  user: IUser;
  users: IUser[];
  buildingsAction: typeof buildingsAction;
  workstationsAction: typeof workstationsAction;
  usersAction: typeof usersAction;
  ptwAction: typeof ptwAction;
  ptwUpdateAction: typeof ptwUpdateAction;
  push: Push;
}

interface PtwsListComponentState {
  ptws: IPtw[];
  ptwsToExport: IPtwPrettied[];
  column: string;
  prettiedPtws: IPtwPrettied[];
  activeFilters: {
    v: number;
    sequentialNumber: string;
    company: string;
    train: string;
    status?: EPtwStatus;
    issuer: string;
    requiredTesting: boolean;
  };
  direction: 1 | -1;
  ptwCount: number;
  activePage: number;
  mounted: boolean;
}

class PPtwsListComponent extends Component<PtwsListComponentProps, PtwsListComponentState> {
  state: PtwsListComponentState = {
    ptws: [],
    ptwsToExport: [],
    column: 'sequentialNumber',
    prettiedPtws: [],
    activeFilters: {
      v: 2,
      sequentialNumber: '',
      company: '',
      train: '',
      issuer: '',
      requiredTesting: undefined,
    },
    direction: -1,
    ptwCount: 0,
    activePage: 1,
    mounted: false,
  };

  componentDidMount(): void {
    this.props.buildingsAction();
    this.props.workstationsAction();
    // this.props.usersAction();

    const activeFiltersString = localStorage.getItem('ptwListFilters');
    let activeFilters = activeFiltersString ? JSON.parse(activeFiltersString) : this.state.activeFilters;

    if (activeFilters.v !== 2) {
      activeFilters = this.state.activeFilters;
    }

    this.setState({ mounted: true, activeFilters }, () => {
      this.loadPtws();
    });
  }

  componentDidUpdate(
    prevProps: Readonly<PtwsListComponentProps>,
    prevState: Readonly<PtwsListComponentState>,
    snapshot?: any
  ): void {
    if (this.state.ptws !== prevState.ptws) {
      this.setState({
        prettiedPtws: this.prettiedPtws(this.state.ptws),
      });
    }
  }

  componentWillUnmount() {
    this.setState({ mounted: false });
    localStorage.setItem('ptwListFilters', JSON.stringify(this.state.activeFilters));
  }

  loadPtws = () => {
    this.setState({ ptws: [], ptwsToExport: [], ptwCount: 0 });
    const { activeFilters } = this.state;

    const _filter: any = {
      company: activeFilters.company !== '' ? activeFilters.company : undefined,
    };

    if (typeof activeFilters.train !== 'undefined' && activeFilters.train !== '') {
      _filter.train = this.props.project.trains
        .filter((train) => train.name?.toLowerCase().includes(activeFilters.train?.toLowerCase()))
        .map(({ _id }) => _id);
    }

    if (typeof activeFilters.status !== 'undefined') {
      _filter.status = activeFilters.status;
    }

    if (typeof activeFilters.issuer !== 'undefined' && activeFilters.issuer !== '') {
      if (this.props.users) {
        _filter.issuer = this.props.users
          .filter(
            (user) =>
              user.name?.toLowerCase().includes(activeFilters.issuer?.toLowerCase()) ||
              user.email?.toLowerCase().includes(activeFilters.issuer?.toLowerCase())
          )
          .map(({ _id }) => _id);
      } else {
        this.props.usersAction();
      }
    }

    if (typeof activeFilters.requiredTesting !== 'undefined') {
      _filter.requiredTesting = activeFilters.requiredTesting;
    }

    if (typeof activeFilters.sequentialNumber !== 'undefined') {
      _filter.sequentialNumber = activeFilters.sequentialNumber;
    }

    Promise.all([
      client.get(`ptws/${this.props.projectId}`, {
        params: {
          _start: (this.state.activePage - 1) * pageSize,
          _limit: pageSize,
          _sort: `${this.state.column}:${this.state.direction}`,
          _filter,
        },
      }),
      client.get(`ptws/${this.props.projectId}/count`, { params: { _filter } }),
    ]).then(([ptwsResponse, countResponse]) => {
      if (this.state.mounted) {
        this.setState({ ptws: ptwsResponse.data, ptwCount: countResponse.data });
      }
    });
    client
      .get<IPtw[]>(`ptws/${this.props.projectId}`, {
        params: {
          _sort: `${this.state.column}:${this.state.direction}`,
          _filter,
        },
      })
      .then((ptwsResponse) => {
        if (this.state.mounted) {
          this.setState({
            ptwsToExport: this.prettiedPtws(ptwsResponse.data),
          });
        }
      });
  };

  prettiedPtws = (ptws: IPtw[]): IPtwPrettied[] => {
    return ptws.map((ptw) => {
      const completionStatus = ptw.completionStatus
        .map((status) => `${moment(status.date).format('DD/MM/YYYY')} - ${status.text}`)
        .join('\u2029')
        .replaceAll(`"`, '');

      const ptwHistory = ptw.history
        .map(
          (update) =>
            `${moment(update.datetime).format('lll')} - ${update.author?.name} changed status to ${
              statusMap[update.status].name
            }`
        )
        .join('\u2029');

      const ptwPrettied: IPtwPrettied = {
        _ptw: ptw,
        sequentialNumber: `#${ptw.sequentialNumber}`,
        name: ptw.name,
        startDate: moment(ptw.startTimestamp).format('DD/MM/YYYY'),
        endDate: moment(ptw.endTimestamp).format('DD/MM/YYYY'),
        company: ptw.company,
        train: this.trainNameForPtw(ptw),
        vehicles: this.trainVehicleNameForPtw(ptw),
        building: this.buildingNameForPtw(ptw),
        workstation: this.workstationNameForPtw(ptw),
        description: ptw.descriptionOfOperations.replaceAll('\n', ' ').replaceAll('"', "'"),
        people: ptw.employees.replaceAll('\n', ','),
        createdAt: moment(ptw.createdAt).format('DD/MM/YYYY'),
        closingDate: extractClosingDate(ptw.history),
        issuer: ptw.issuer.name,
        ehs: typeof ptw.ehs === 'object' ? ptw.ehs : undefined,
        status: statusMap[ptw.status].name,
        qualifiedUsers: ptw.qualifiedUsers,
        requiredTesting: ptw.requiredTesting ? 'YES' : 'NO',
        testPassed: typeof ptw.testPassed !== 'undefined' ? (ptw.testPassed ? 'Test passed' : 'Test not passed') : '',
        requiredQualityTesting: ptw.requiredQualityTesting ? 'YES' : 'NO',
        extraWork: ptw.history.find((h) => h.status === EPtwStatus.RICOLLAUDO_EXTRA_R) ? 'YES' : 'NO',
        pc9: ptw.pc9 || '',
        deletionNotes: ptw.deletionNotes,
        completionStatus,
        ptwHistory,
      };
      return ptwPrettied;
    });
  };

  isEditable = (ptw: IPtw) =>
    [EPtwStatus.NUOVO, EPtwStatus.ACCETTAZIONE_N, EPtwStatus.ACCETTAZIONE_QUALITA_N].includes(ptw.status) &&
    !this.props.project.assignees
      .filter((a) => a.user._id === this.props.user._id)
      .some((a) => a.group === EUserGroup.PRTCM_READONLY);

  handleFilter = (filterColumn: string, filterValue: string | boolean | EPtwStatus) => {
    const { activeFilters } = this.state;

    const newFilters = {
      ...activeFilters,
      [filterColumn]: filterValue,
    };

    this.setState(
      {
        activeFilters: newFilters,
        activePage: 1,
      },
      () => {
        this.loadPtws();
      }
    );
  };

  handleSort = (clickedColumn: string) => () => {
    const { column, direction } = this.state;

    const newState = {};

    if (column !== clickedColumn) {
      this.setState({
        column: clickedColumn,
        direction: 1,
      });
    } else {
      this.setState({
        direction: direction === 1 ? -1 : 1,
      });
    }

    this.setState(newState, () => {
      this.loadPtws();
    });
  };

  handlePageChange = (event: React.MouseEvent<HTMLAnchorElement>, data: PaginationProps) => {
    this.setState(
      {
        activePage: data.activePage as number,
      },
      () => {
        this.loadPtws();
      }
    );
  };

  ptwsForCsv = (ptws: IPtwPrettied[]) => {
    return ptws.map((ptw) => ({
      ...ptw,
      ehs: ptw.ehs ? 'YES' : 'NO',
    }));
  };

  handleSelectPtw = (ptwId: string) => {
    this.props.ptwAction(ptwId);
    this.props.push(`/projects/${this.props.projectId}/${ptwId}`);
  };

  trainNameForPtw = (ptw: IPtw) => {
    return this.props.project.trains.find((train) => train._id === ptw.train)
      ? this.props.project.trains.find((train) => train._id === ptw.train).name
      : '';
  };

  trainVehicleNameForPtw(ptw: IPtw): string[] {
    const train = this.props.project.trains.find((train) => train._id === ptw.train);
    if (train) {
      return train.vehicles.filter((vehicle) => ptw.vehicles.includes(vehicle._id)).map((vehicle) => vehicle.name);
    } else {
      return [];
    }
  }

  buildingNameForPtw(ptw: IPtw): string {
    const building = (this.props.buildings || []).find((building) => building._id === ptw.building);
    return building ? building.name : '';
  }

  workstationNameForPtw(ptw: IPtw): string {
    const workstation = (this.props.workstations || []).find((workstation) => workstation._id === ptw.workstation);
    return workstation ? workstation.name : '';
  }

  handleEhsChange(ptw: IPtw, ehs: IEhs) {
    this.props.ptwUpdateAction({ ...ptw, ehs });
    if (ehs.status === EEhsStatus.CHIUSURA_Y) {
      this.props.ptwAction(ptw._id);
    }
  }

  onDeleteModalUpdate = () => {
    this.loadPtws();
  };

  render() {
    const { column, direction, activeFilters, prettiedPtws, ptwsToExport } = this.state;
    const { t, buildings, project, user } = this.props;

    return (
      <Segment>
        <Grid columns="equal">
          <Grid.Column>
            <Header as="h2">
              <TranslationComponent stringKey={'Ptw list ({{ptwCount}})'} values={{ ptwCount: this.state.ptwCount }} />
            </Header>
          </Grid.Column>
          <Grid.Column textAlign="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CsvDownloader
              filename="ptws-export"
              separator={';'}
              wrapColumnChar={'"'}
              columns={csvColumns}
              datas={this.ptwsForCsv(ptwsToExport) as Array<{ [key: string]: any }>}
              suffix={''}
              prefix={''}
            >
              <Button>
                <TranslationComponent stringKey={'Export'} />
              </Button>
            </CsvDownloader>
          </Grid.Column>
        </Grid>

        <Grid columns="equal">
          <Grid.Row>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Company')}
                value={activeFilters.company}
                onChange={(e) => {
                  this.handleFilter('company', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Train')}
                value={activeFilters.train}
                onChange={(e) => {
                  this.handleFilter('train', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <PtwStatusDropdown
                value={activeFilters.status as EPtwStatus}
                onChange={(value) => {
                  this.handleFilter('status', value);
                }}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Issuer')}
                value={activeFilters.issuer}
                onChange={(e) => {
                  this.handleFilter('issuer', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">{t('Richiesto ricollaudo')}</Header>
              <Dropdown
                placeholder={t('Enter the filter.')}
                selection
                options={[
                  { key: 0, text: t('Vuoto'), value: undefined },
                  { key: 1, text: t('Si'), value: true },
                  { key: 2, text: t('No'), value: false },
                ]}
                value={undefined}
                onChange={(e, data) => {
                  this.handleFilter('requiredTesting', data.value !== undefined ? Boolean(data.value) : undefined);
                }}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Sequential number')}
                value={activeFilters.sequentialNumber}
                onChange={(e) => {
                  this.handleFilter('sequentialNumber', e.target.value.replaceAll('#', ''));
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Table sortable celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={column === 'sequentialNumber' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('sequentialNumber')}
              >
                <TranslationComponent stringKey={'Sequential number'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'name' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('name')}
              >
                <TranslationComponent stringKey={'Name'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'company' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('company')}
              >
                <TranslationComponent stringKey={'Company'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'train' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('train')}
              >
                <TranslationComponent stringKey={'Train'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'vehicles' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('vehicles')}
              >
                <TranslationComponent stringKey={'Vehicles'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'createdAt' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('createdAt')}
              >
                <TranslationComponent stringKey={'Submission Date'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'issuer' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('issuer')}
              >
                <TranslationComponent stringKey={'Issuer'} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <TranslationComponent stringKey={'EHS'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'status' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('status')}
              >
                <TranslationComponent stringKey={'State'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'requiredTesting' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('requiredTesting')}
              >
                <TranslationComponent stringKey={'Richiesto ricollaudo'} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <TranslationComponent stringKey={'Deletion notes'} />
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {prettiedPtws.map((ptw) => (
              <Table.Row key={ptw._ptw._id}>
                <Table.Cell onClick={() => this.handleSelectPtw(ptw._ptw._id)} className="cursor-pointer">
                  {ptw.sequentialNumber}
                </Table.Cell>
                <Table.Cell>{ptw.name}</Table.Cell>
                <Table.Cell>{ptw.company}</Table.Cell>
                <Table.Cell>{ptw.train}</Table.Cell>
                <Table.Cell>
                  {ptw.vehicles.map((vehicleName, i) => (
                    <div key={`{$ptw._ptw._id}V${i}`}>{vehicleName}</div>
                  ))}
                </Table.Cell>
                <Table.Cell>{ptw.createdAt}</Table.Cell>
                <Table.Cell>{ptw.issuer}</Table.Cell>
                <Table.Cell>
                  {ptw.ehs ? (
                    <EhsCreateModal
                      buildings={buildings}
                      ehs={ptw.ehs}
                      onEhs={(ehs) => this.handleEhsChange(ptw._ptw, ehs)}
                    />
                  ) : null}
                </Table.Cell>
                <PtwsListStatusComponent ptw={ptw} />
                <Table.Cell>{ptw.requiredTesting}</Table.Cell>
                <Table.Cell>
                  <div>{ptw.deletionNotes}</div>
                  {(ptw._ptw.files as unknown as Record<string, IPtwFile>)?.['deletionAttachment'] && (
                    <div>
                      <FileDownloadLink path={`/ptw/${ptw._ptw._id}/file/deletionAttachment`}>
                        <TranslationComponent stringKey={'Open attachment'} />
                      </FileDownloadLink>
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell singleLine textAlign={'center'}>
                  {this.isEditable(ptw._ptw) && <PtwEditModal ptw={ptw._ptw} />}
                  {ptwUtils.isDeletable(project, user, ptw._ptw) && (
                    <PtwDeleteModal ptw={ptw._ptw} onDelete={this.onDeleteModalUpdate} />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <Pagination
          activePage={this.state.activePage}
          totalPages={Math.round(this.state.ptwCount / pageSize)}
          onPageChange={this.handlePageChange}
        />
      </Segment>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  project: appState(state).project,
  buildings: appState(state).buildings,
  workstations: appState(state).workstations,
  user: appState(state).user,
  users: appState(state).users,
});

export const PtwsListComponent = connect(mapStateToProps, {
  buildingsAction,
  workstationsAction,
  ptwAction,
  ptwUpdateAction,
  ptwEditAction,
  ptwDeleteAction,
  usersAction,
  push,
})(withTranslation()(PPtwsListComponent));
